import { HttpInterceptorFn, HttpEvent } from '@angular/common/http';

import { Observable } from 'rxjs';
import { shareReplay, finalize } from 'rxjs/operators';

import { CLEAR, DEDUPE } from './depute.token';

const inflight = new Map<string, Observable<HttpEvent<any>>>();

const cache = new Map<string, { at: number; obs: Observable<HttpEvent<any>> }>();
const TTL = 5_000;

export const dedupeInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET' || !req.context.get(DEDUPE)) return next(req);
  const key = req.urlWithParams;
  const existing = inflight.get(key);
  if (existing) return existing;

  const shared$ = next(req).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    finalize(() => inflight.delete(key)),
  );
  inflight.set(key, shared$);
  return shared$;
};

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  const tag = req.context.get(CLEAR);
  if (tag) clearByTag(tag);

  if (req.method !== 'GET' || !req.context.get(DEDUPE)) return next(req);
  const key = makeKey(req);
  const now = Date.now();

  const hit = cache.get(key);
  if (hit && now - hit.at < TTL) return hit.obs;
  const shared$ = next(req).pipe(shareReplay({ bufferSize: 1, refCount: false, windowTime: TTL }));
  cache.set(key, { at: now, obs: shared$ });
  return shared$;
};

// eslint-disable-next-line import/order
import { HttpRequest, HttpParams } from '@angular/common/http';

export function makeKey(
  req: HttpRequest<any>,
  opts?: {
    includeHeaders?: string[];
    includeBodyForMethods?: string[];
  },
): string {
  const method = req.method.toUpperCase();
  const url = req.url;
  const qp = buildSortedQuery(req.params);

  let key = `${method}|${url}|${qp}`;

  if (opts?.includeHeaders?.length) {
    const parts = opts.includeHeaders
      .map(h => {
        const all = req.headers.getAll(h) ?? [];
        return `${h.toLowerCase()}=${all.join(',')}`;
      })
      .join('&');
    key += `|H:${parts}`;
  }

  if (opts?.includeBodyForMethods?.includes(method)) {
    key += `|B:${hash(stableStringify(req.body))}`;
  }

  return key;
}

function buildSortedQuery(params: HttpParams): string {
  if (!params) return '';
  const keys = params.keys().slice().sort();
  const pairs: string[] = [];
  for (const k of keys) {
    const vals = (params.getAll(k) ?? []).slice().sort();
    pairs.push(`${encodeURIComponent(k)}=${vals.map(v => encodeURIComponent(v)).join(',')}`);
  }
  return pairs.join('&');
}

function stableStringify(x: any): string {
  if (x === null || typeof x !== 'object') return JSON.stringify(x);
  if (Array.isArray(x)) return `[${x.map(stableStringify).join(',')}]`;
  const keys = Object.keys(x).sort();
  return `{${keys.map(k => JSON.stringify(k) + ':' + stableStringify(x[k])).join(',')}}`;
}

function hash(s: string): string {
  let h = 0;
  // eslint-disable-next-line no-bitwise
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return String(h);
}

function clearByTag(tag: string) {
  const maps: Array<Map<string, unknown>> = [cache as any, inflight as any];
  const needle = tag.toLowerCase();
  for (const m of maps) {
    for (const k of Array.from(m.keys())) {
      if (k.toLowerCase().includes(needle)) m.delete(k);
    }
  }
}
